/**
 * PR-21 — Extended User Profile (first-party data store).
 * Persisted client-side (localStorage) to coexist with the existing
 * useAuth/session model. Server sync happens via existing tables
 * (user_registrations / fan_insights / audit_master) when available.
 */
import { useSyncExternalStore } from "react";
import { trackEvent } from "@/lib/audit";

export type FanType = "casual" | "passionate" | "ultra" | "family" | "analyst";

export interface UserProfile {
  user_id: string;
  // identity
  first_name: string;
  last_name: string;
  display_name: string;
  username: string;
  email: string;
  avatar_url: string | null;
  // location
  country: string;
  state: string;
  city: string;
  timezone: string;
  language: "ES" | "EN" | "PT";
  // profile
  birth_year: number | null;
  gender: "female" | "male" | "nonbinary" | "prefer_not" | "";
  favorite_team: string;
  favorite_national_team: string;
  favorite_player: string;
  fan_type: FanType | "";
  // preferences
  favorite_sports: string[];
  favorite_content: string[];
  notification_frequency: "high" | "medium" | "low" | "off";
  commercial_interests: string[];
  // consumption
  favorite_foods: string[];
  favorite_beverages: string[];
  favorite_brands: string[];
  delivery_apps: string[];
  coupon_interest: boolean;
  // sponsors
  sponsor_categories: string[];
  accepts_promotions: boolean;
  reward_preference: "vouchers" | "physical" | "points" | "experiences" | "";
  // consent
  terms_accepted_at: string | null;
  privacy_accepted_at: string | null;
  marketing_opt_in: boolean;
  analytics_opt_in: boolean;
  // attribution
  registration_source: string;
  acquisition_campaign: string;
  referral_code: string;
  // theme
  theme_palette: string;
  // meta
  created_at: string;
  updated_at: string;
  onboarding_completed: boolean;
}

const KEY = "user_profile_v1";

function uuid(): string {
  try { return crypto.randomUUID(); } catch { return `u-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

export function emptyProfile(): UserProfile {
  const now = new Date().toISOString();
  return {
    user_id: uuid(),
    first_name: "", last_name: "", display_name: "", username: "", email: "",
    avatar_url: null,
    country: "", state: "", city: "",
    timezone: typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC",
    language: "ES",
    birth_year: null, gender: "",
    favorite_team: "", favorite_national_team: "", favorite_player: "", fan_type: "",
    favorite_sports: [], favorite_content: [], notification_frequency: "medium", commercial_interests: [],
    favorite_foods: [], favorite_beverages: [], favorite_brands: [], delivery_apps: [], coupon_interest: true,
    sponsor_categories: [], accepts_promotions: true, reward_preference: "",
    terms_accepted_at: null, privacy_accepted_at: null, marketing_opt_in: false, analytics_opt_in: true,
    registration_source: "organic", acquisition_campaign: "", referral_code: "",
    theme_palette: "dark_premium",
    created_at: now, updated_at: now, onboarding_completed: false,
  };
}

function read(): UserProfile | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) as UserProfile : null;
  } catch { return null; }
}

let state: UserProfile | null = read();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function getUserProfile(): UserProfile | null { return state; }

export function saveUserProfile(patch: Partial<UserProfile>) {
  const base = state ?? emptyProfile();
  state = { ...base, ...patch, updated_at: new Date().toISOString() };
  if (typeof window !== "undefined") window.localStorage.setItem(KEY, JSON.stringify(state));
  void trackEvent("profile_updated", { fields: Object.keys(patch) });
  emit();
}

export function deleteUserProfile() {
  state = null;
  if (typeof window !== "undefined") window.localStorage.removeItem(KEY);
  emit();
}

export function exportUserProfile(): string {
  return JSON.stringify(state ?? {}, null, 2);
}

export function useUserProfile(): UserProfile | null {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => state,
    () => state,
  );
}

// Sample option catalogs --------------------------------------------------
export const SPORTS = ["football", "basketball", "tennis", "baseball", "f1", "boxing"];
export const CONTENT_TYPES = ["highlights", "analysis", "interviews", "stats", "betting_tips"];
export const COMMERCIAL_INTERESTS = ["beverages", "fastfood", "electronics", "travel", "banking", "betting", "fashion", "tech"];
export const FOODS = ["pizza", "burgers", "tacos", "wings", "sushi", "bbq", "vegan"];
export const BEVERAGES = ["beer", "soda", "energy_drinks", "coffee", "whisky", "tequila", "wine"];
export const BRANDS_HINTS = ["coca_cola", "pepsi", "heineken", "corona", "modelo", "redbull", "nike", "adidas"];
export const DELIVERY_APPS = ["rappi", "ubereats", "doordash", "didi_food", "ifood"];
export const SPONSOR_CATEGORIES = ["beverages", "betting", "automotive", "fintech", "fastfood", "telecom", "streaming"];
export const FAN_TYPES: FanType[] = ["casual", "passionate", "ultra", "family", "analyst"];